import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { SignIn, GoogleLogo, SpinnerGap } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { useI18n } from '@/lib/i18n';

interface LoginButtonProps {
  className?: string;
  variant?: 'default' | 'outline' | 'ghost';
  size?: 'default' | 'sm' | 'lg';
  showDialog?: boolean;
}

export function LoginButton({ className, variant = 'default', size = 'sm', showDialog = true }: LoginButtonProps = {}) {
  const { user, userProfile, loading, signInWithGoogle, signInWithEmailPassword, signOut } = useAuth();
  const { t } = useI18n();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [localEmail, setLocalEmail] = useState('local.tester@planetearth.help');
  const [localPassword, setLocalPassword] = useState('PlanetEarthLocal!2026');

  const getSignInErrorMessage = (error: unknown) => {
    const errorCode =
      typeof error === 'object' && error !== null && 'code' in error ? String((error as { code?: unknown }).code) : '';

    switch (errorCode) {
      case 'auth/unauthorized-domain':
        return t('auth.unauthorizedDomain');
      case 'auth/operation-not-allowed':
        return t('auth.providerDisabled');
      case 'auth/invalid-api-key':
      case 'auth/app-not-authorized':
      case 'auth/configuration-not-found':
        return t('auth.configMismatch');
      case 'auth/popup-closed-by-user':
        return t('auth.popupClosed');
      case 'auth/network-request-failed':
        return t('auth.networkFailed');
      default:
        return t('auth.signInFailed');
    }
  };

  const handleSignIn = async () => {
    setIsSigningIn(true);
    try {
      await signInWithGoogle();
      setIsDialogOpen(false);
    } catch (error) {
      console.error('Sign in failed:', error);
      toast.error(getSignInErrorMessage(error));
    } finally {
      setIsSigningIn(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Sign out failed:', error);
    }
  };

  const handleLocalSignIn = async () => {
    setIsSigningIn(true);
    try {
      await signInWithEmailPassword(localEmail, localPassword);
      setIsDialogOpen(false);
      toast.success(t('auth.localSuccess'));
    } catch (error) {
      console.error('Local sign in failed:', error);
      toast.error(t('auth.localFailure'));
    } finally {
      setIsSigningIn(false);
    }
  };

  if (loading) {
    return (
      <Button variant="ghost" size="sm" disabled>
        <SpinnerGap size={18} className="animate-spin" />
      </Button>
    );
  }

  if (user && userProfile) {
    return (
      <div className="flex items-center gap-3">
        {userProfile.isAdmin && (
          <span className="hidden sm:inline text-xs font-semibold px-2 py-1 bg-primary/10 text-primary rounded-full">
            {t('dashboard.admin')}
          </span>
        )}
        <div className="hidden sm:flex items-center gap-2">
          {userProfile.photoURL && (
            <img
              src={userProfile.photoURL}
              alt={userProfile.displayName || t('dashboard.user')}
              className="w-8 h-8 rounded-full border-2 border-border"
            />
          )}
          <span className="text-sm font-medium text-foreground max-w-[150px] truncate">
            {userProfile.displayName || userProfile.email}
          </span>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleSignOut}
          className="hover:bg-destructive/10 hover:text-destructive hover:border-destructive/30"
        >
          {t('auth.signOut')}
        </Button>
      </div>
    );
  }

  return (
    <>
      <Button
        variant={variant}
        size={size}
        aria-label={t('auth.signIn')}
        title={t('auth.signIn')}
        onClick={() => (showDialog ? setIsDialogOpen(true) : handleSignIn())}
        className={cn('shadow-md hover:shadow-lg transition-all', className)}
      >
        <SignIn size={16} className="sm:mr-2" />
        <span className="hidden sm:inline">{t('auth.signIn')}</span>
      </Button>

      {showDialog && (
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold">{t('auth.welcomeBack')}</DialogTitle>
              <DialogDescription className="text-base">{t('auth.dialogBody')}</DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <Button
                onClick={handleSignIn}
                disabled={isSigningIn}
                className="w-full h-12 text-base font-semibold shadow-md hover:shadow-lg transition-all"
                size="lg"
              >
                {isSigningIn ? (
                  <>
                    <SpinnerGap size={20} className="mr-2 animate-spin" />
                    {t('auth.signingIn')}
                  </>
                ) : (
                  <>
                    <GoogleLogo size={20} className="mr-2" weight="bold" />
                    {t('auth.continueGoogle')}
                  </>
                )}
              </Button>

              {import.meta.env.DEV && import.meta.env.VITE_USE_FIREBASE_EMULATORS === 'true' && (
                <div className="space-y-3 rounded-lg border border-dashed border-primary/30 bg-primary/5 p-4">
                  <div>
                    <p className="text-sm font-semibold">{t('auth.localAccount')}</p>
                    <p className="text-xs text-muted-foreground">{t('auth.localAccountBody')}</p>
                  </div>
                  <Input
                    type="email"
                    value={localEmail}
                    onChange={(event) => setLocalEmail(event.target.value)}
                    aria-label={t('auth.localEmail')}
                  />
                  <Input
                    type="password"
                    value={localPassword}
                    onChange={(event) => setLocalPassword(event.target.value)}
                    aria-label={t('auth.localPassword')}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleLocalSignIn}
                    disabled={isSigningIn || !localEmail || !localPassword}
                    className="w-full"
                  >
                    {t('auth.localSignIn')}
                  </Button>
                </div>
              )}

              <div className="text-center">
                <p className="text-xs text-muted-foreground">{t('auth.legal')}</p>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
